import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { BreedPhoto } from '../../components/funnel/BreedPhoto';
import { FunnelPillButton } from '../../components/funnel/FunnelPillButton';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { buildWhatsAppHref } from '../../components/funnel/whatsappMessage';
import { Pathology } from '../../types';
import {
  PATHOLOGY_CATALOG,
  getPathologyInsight,
  getTierCopy,
  pathologyByKey,
  mapCatalogKeyToPathologyEnum,
} from './pathologyCatalog';
import { searchConditions } from '../data/conditionsCatalog';
import type { ConditionEntry } from '../data/conditionsCatalog';
import { joinWaitlist } from '../../services/geo';
import type { PathologyTier } from '../../types';
import { useFunnelMode } from '../FunnelModeContext';

// ── Constants ─────────────────────────────────────────────────────────────────

type GateAnswer = 'si' | 'no' | 'no_se';

const TIER_3_KEYS = PATHOLOGY_CATALOG.filter((p) => p.tier === 3).map((p) => p.key);
const TIER_2_KEYS = PATHOLOGY_CATALOG.filter((p) => p.tier === 2).map((p) => p.key);

// ── PathologyModal (private sub-component) ────────────────────────────────────

interface PathologyModalProps {
  icon: string;
  titleId: string;
  title: string;
  body: string;
  whatsappUrl: string;
  whatsappText: string;
  whatsappLabel: string;
  secondaryLabel: string;
  onSecondary: () => void;
  onDismiss: () => void;
}

const PathologyModal: React.FC<PathologyModalProps> = ({
  icon,
  titleId,
  title,
  body,
  whatsappUrl,
  whatsappText,
  whatsappLabel,
  secondaryLabel,
  onSecondary,
  onDismiss,
}) => (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4" onClick={onDismiss}>
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="bg-white rounded-[20px] p-6 max-w-sm w-full shadow-xl space-y-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-center">
        <span className="text-3xl">{icon}</span>
      </div>
      <h3 id={titleId} className="font-fredoka text-lg font-semibold text-black text-center">
        {title}
      </h3>
      <p className="font-poppins text-sm text-black/70 text-center leading-snug">{body}</p>
      <div className="flex flex-col gap-2 pt-1">
        {whatsappUrl && (
          <a
            href={`${whatsappUrl}?text=${whatsappText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] text-white font-fredoka font-semibold rounded-[14px] hover:bg-[#20b859] transition-colors"
          >
            <span>💬</span>
            <span>{whatsappLabel}</span>
          </a>
        )}
        <button
          type="button"
          onClick={onSecondary}
          className="w-full py-3 bg-gray-100 text-black/70 font-fredoka font-medium rounded-[14px] hover:bg-gray-200 transition-colors"
        >
          {secondaryLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const P8Salud: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { t, config } = useAppConfig();
  const funnelMode = useFunnelMode();
  const isKcalMode = funnelMode === 'kcal';
  const excludedConditions: string[] = config.funnel_config?.excluded_conditions ?? [];

  const nombre = formData.nombre || 'tu perro';
  // Único origen del teléfono: contact_rules.whatsapp_phone. Vacío = sin WhatsApp.
  const whatsappUrl = buildWhatsAppHref(config?.contact_rules?.whatsapp_phone || '', '');

  /**
   * Gate state: null = initial / not chosen yet.
   * Lazy initializer for back-nav: if formData.pathologies contains something
   * other than NINGUNA, the user must have previously answered 'si'.
   */
  const [gate, setGate] = useState<GateAnswer | null>(() => {
    if (formData.healthGateAnswer) return formData.healthGateAnswer;
    const saved = formData.pathologies || [];
    if (saved.length > 0 && !saved.includes(Pathology.NINGUNA)) return 'si';
    return null;
  });

  /**
   * Selected catalog keys (multi-select).
   * Lazy init from formData for back-nav state restoration.
   */
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => {
    const saved = formData.pathologies || [];
    if (saved.includes(Pathology.NINGUNA) || saved.length === 0) return new Set();
    // Reverse-map enum values back to catalog keys
    const keys = new Set<string>();
    PATHOLOGY_CATALOG.forEach((entry) => {
      const enumVal = mapCatalogKeyToPathologyEnum(entry.key);
      if (enumVal && saved.includes(enumVal)) {
        keys.add(entry.key);
      }
    });
    return keys;
  });

  /** Which tier warning modal to show: null = none. */
  const [modalTier, setModalTier] = useState<PathologyTier | null>(null);

  /** The catalog key that last triggered a modal. */
  const [lastModalKey, setLastModalKey] = useState<string | null>(null);

  /** Why we ask — info modal. */
  const [showWhyModal, setShowWhyModal] = useState(false);

  /** Mobile-only: trigger que abre el panel de insights como popup. En
   *  desktop el panel sigue inline en el sidebar. */
  const [showInsightsModal, setShowInsightsModal] = useState(false);

  const [attempted, setAttempted] = useState(false);

  /** Popup "Otra condición" — autocompletar + comentarios + flujo de exclusión. */
  const [showOtraModal, setShowOtraModal] = useState(false);
  const [otraSearch, setOtraSearch] = useState<string>('');
  const [otraComment, setOtraComment] = useState<string>('');
  const [otraSelected, setOtraSelected] = useState<ConditionEntry | null>(null);
  const [otraDropdownOpen, setOtraDropdownOpen] = useState(false);
  const [otraExcludedEmail, setOtraExcludedEmail] = useState('');
  const [otraExcludedSubmitting, setOtraExcludedSubmitting] = useState(false);
  const [otraExcludedSubmitted, setOtraExcludedSubmitted] = useState(false);
  const [otraExcludedAlreadyRegistered, setOtraExcludedAlreadyRegistered] = useState(false);
  const [otraExcludedError, setOtraExcludedError] = useState<string | null>(null);
  const otraInputRef = useRef<HTMLInputElement>(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Computes the Pathology enum array from a set of catalog keys. */
  const pathologiesFromKeys = (keys: Set<string>): Pathology[] => {
    if (keys.size === 0) return [Pathology.NINGUNA];
    const enumValues = new Set<Pathology>();
    keys.forEach((key) => {
      const ev = mapCatalogKeyToPathologyEnum(key);
      if (ev) enumValues.add(ev);
      // Keys with null enum (Tier 3 items) are acknowledged via the modal
      // but not stored in formData.pathologies — see pathologyCatalog.ts.
    });
    return enumValues.size > 0 ? Array.from(enumValues) : [Pathology.NINGUNA];
  };

  const keepSubPathologiesFor = (pathologies: Pathology[]): Record<string, string[]> => {
    const selected = new Set(pathologies.map(String));
    return Object.fromEntries(
      Object.entries(formData.subPathologies || {}).filter(([pathology]) => selected.has(pathology)),
    );
  };

  const handleOtraConfirm = () => {
    const conditionLabel = otraSelected?.label ?? otraSearch.trim();
    const text = [conditionLabel, otraComment.trim()].filter(Boolean).join(' — ');
    const next = new Set(selectedKeys);
    next.add('otra');
    setSelectedKeys(next);
    updateFormData({ healthGateAnswer: 'si', otherPathologyText: text, pathologies: pathologiesFromKeys(next) });
    setShowOtraModal(false);
  };

  const handleOtraRemove = () => {
    const next = new Set(selectedKeys);
    next.delete('otra');
    setSelectedKeys(next);
    const nextPathologies = pathologiesFromKeys(next);
    updateFormData({
      healthGateAnswer: 'si',
      pathologies: nextPathologies,
      subPathologies: keepSubPathologiesFor(nextPathologies),
      otherPathologyText: '',
    });
    setOtraSearch('');
    setOtraComment('');
    setOtraSelected(null);
    setOtraExcludedSubmitted(false);
    setOtraExcludedEmail('');
    setOtraExcludedError(null);
  };

  const handleOtraSearchChange = (value: string) => {
    setOtraSearch(value);
    setOtraSelected(null);
    setOtraDropdownOpen(value.trim().length >= 2);
  };

  const handleOtraSelectCondition = (entry: ConditionEntry) => {
    setOtraSelected(entry);
    setOtraSearch(entry.label);
    setOtraDropdownOpen(false);
    setOtraExcludedSubmitted(false);
    setOtraExcludedEmail('');
    setOtraExcludedError(null);
  };

  const handleSubmitOtraExcluded = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otraExcludedEmail.trim() || otraExcludedSubmitting) return;
    setOtraExcludedSubmitting(true);
    setOtraExcludedError(null);
    try {
      const conditionLabel = otraSelected?.label ?? otraSearch.trim();
      const contextParts = [
        'otra-excluida',
        `condicion=${conditionLabel}`,
        formData.peso ? `peso=${formData.peso}kg` : null,
        formData.raza ? `raza=${formData.raza}` : null,
        formData.ageCategory ? `edad=${formData.ageCategory}` : null,
      ].filter((part): part is string => Boolean(part));
      const res = await joinWaitlist({
        email: otraExcludedEmail.trim(),
        petName: nombre,
        animalType: 'perro',
        country: 'ES',
        petWeightKg: formData.peso,
        context: contextParts.join(' | '),
      });
      setOtraExcludedAlreadyRegistered(!!res?.already_registered);
      setOtraExcludedSubmitted(true);
      updateFormData({ email: otraExcludedEmail.trim() });
    } catch (err) {
      console.error('otra_excluida_waitlist_failed', err);
      setOtraExcludedError(t('p8.tier2_error', 'No pudimos enviar tu email. Inténtalo de nuevo.'));
    } finally {
      setOtraExcludedSubmitting(false);
    }
  };

  const handleGate = (answer: GateAnswer) => {
    setGate(answer);
    if (answer === 'no') {
      // "No" stays on screen and just enables the CTA (no screen auto-advances).
      updateFormData({ healthGateAnswer: answer, pathologies: [Pathology.NINGUNA], subPathologies: {} });
      return;
    }
    if (answer === 'no_se') {
      // Save to formData but stay on screen so the reassuring callout is visible.
      // The CTA (canContinue = true for no_se) lets the user confirm and move on.
      updateFormData({ healthGateAnswer: answer, pathologies: [Pathology.NINGUNA], subPathologies: {} });
      return;
    }
    updateFormData({ healthGateAnswer: answer });
  };

  const handlePathologyToggle = (key: string) => {
    const meta = pathologyByKey(key);
    if (!meta) return;

    // Compute next state imperatively (do NOT call updateFormData inside setState)
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      // Tier 2 (renal/hepática/pancreática): handlePathologyToggle ya no setea modalTier,
      // así que PathologyModal con tier=2 no se activa desde la ruta normal. El bloqueo
      // se hace a través del modal popup de waitlist (hasAnyTier2 && gate === 'si').
    }

    setSelectedKeys(next);
    const nextPathologies = pathologiesFromKeys(next);
    updateFormData({
      healthGateAnswer: 'si',
      pathologies: nextPathologies,
      subPathologies: keepSubPathologiesFor(nextPathologies),
    });
  };

  const handleSubPathologyToggle = (pathology: string, subPathology: string) => {
    const next = new Set(formData.subPathologies?.[pathology] || []);
    if (next.has(subPathology)) next.delete(subPathology);
    else next.add(subPathology);
    updateFormData({
      subPathologies: {
        ...(formData.subPathologies || {}),
        [pathology]: Array.from(next),
      },
    });
  };

  const handleModalContinue = useCallback(() => {
    // User acknowledged warning/escalation — close modal and proceed normally.
    setModalTier(null);
    setLastModalKey(null);
  }, []);

  const handleModalDismiss = useCallback(() => {
    // User closed modal without proceeding — deselect the triggering key.
    if (lastModalKey) {
      const next = new Set(selectedKeys);
      next.delete(lastModalKey);
      setSelectedKeys(next);
      const nextPathologies = pathologiesFromKeys(next);
      updateFormData({
        healthGateAnswer: 'si',
        pathologies: nextPathologies,
        subPathologies: keepSubPathologiesFor(nextPathologies),
      });
    }
    setModalTier(null);
    setLastModalKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastModalKey, selectedKeys]);

  const handleWhyModalClose = useCallback(() => setShowWhyModal(false), []);

  // ── Escape-to-close for all modals ────────────────────────────────────────

  useEffect(() => {
    if (modalTier === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleModalDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalTier, handleModalDismiss]);

  useEffect(() => {
    if (!showWhyModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleWhyModalClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showWhyModal, handleWhyModalClose]);

  useEffect(() => {
    if (!showOtraModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowOtraModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showOtraModal]);

  // Tier 3 sigue como en código previo (no hay tier 3 hoy, gancho preparado).
  const hasAnyTier3 = Array.from(selectedKeys).some((k) => TIER_3_KEYS.includes(k));

  // Tier 2 (renal/hepática/pancreática) bloquea continuación: aún no
  // tenemos recetas específicas para esas condiciones, así que en lugar
  // de seguir mostramos un formulario de waitlist con email + datos del
  // perro para tracking de demanda.
  const tier2SelectedKeys = Array.from(selectedKeys).filter((k) => TIER_2_KEYS.includes(k));
  const hasAnyTier2 = tier2SelectedKeys.length > 0;
  const selectedPathologyValues = new Set(pathologiesFromKeys(selectedKeys).map(String));
  const selectedSubPathologyGroups = (config.funnel_config?.pathology_options || []).filter(
    (option) => selectedPathologyValues.has(option.value) && (option.sub_pathologies?.length || 0) > 0,
  );

  const canContinue =
    isKcalMode ||
    gate === 'no' ||
    gate === 'no_se' ||
    (gate === 'si' && selectedKeys.size > 0 && !hasAnyTier3 && !hasAnyTier2);

  // Tier 2 waitlist form state
  const [tier2Email, setTier2Email] = useState(formData.email || '');
  const [tier2Submitting, setTier2Submitting] = useState(false);
  const [tier2Submitted, setTier2Submitted] = useState(false);
  const [tier2AlreadyRegistered, setTier2AlreadyRegistered] = useState(false);
  const [tier2Dismissed, setTier2Dismissed] = useState(false);
  const [tier2Error, setTier2Error] = useState<string | null>(null);

  const tier2Labels = tier2SelectedKeys.map((k) => pathologyByKey(k)?.label || k).join(', ');

  // Insights de patologías Tier 1 seleccionadas — explica por qué la comida
  // natural encaja y qué suplemento ayuda. Se muestran en sidebar (desktop)
  // o inline tras el grid (móvil) para reforzar autoridad veterinaria.
  const tier1Insights = Array.from(selectedKeys)
    .filter((k) => !TIER_2_KEYS.includes(k) && !TIER_3_KEYS.includes(k))
    .map((k) => {
      const insight = getPathologyInsight(k, t);
      return {
        key: k,
        label: pathologyByKey(k)?.label || k,
        why: insight?.why || '',
        supplement: insight?.supplement,
      };
    })
    .filter((insight) => Boolean(insight.why));

  // Insights panel: el label de cada patología va inline ("Para perros con
  // Renal:") en lugar de como heading separado para evitar colisiones con
  // el grid card del mismo nombre en queries por accesible name. El label
  // dentro de la card del grid sigue siendo "Renal" a secas; aquí va con
  // contexto añadido para que getByText('Renal') solo apunte al grid.
  const insightsPanel =
    tier1Insights.length > 0 ? (
      <div className="bg-white rounded-2xl shadow-sm border border-[#90D1E8]/40 p-4 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#008BBC' }}>
          {t('p8.insights_label', 'Lo que hace nuestra comida')}
        </p>
        <ul className="space-y-3">
          {tier1Insights.map((ins) => (
            <li key={ins.key} className="space-y-1">
              <p className="font-poppins text-xs text-black/70 leading-snug">
                <span className="font-fredoka font-semibold text-black/85">{ins.label}:</span> {ins.why}
              </p>
              {ins.supplement && (
                <p className="font-poppins text-xs text-[#008BBC] flex items-center gap-1">
                  <span aria-hidden="true">💊</span>
                  <span>
                    {t('p8.insights_supplement_prefix', 'Suplemento sugerido:')} {ins.supplement}
                  </span>
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const handleSubmitTier2 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tier2Email.trim() || tier2Submitting) return;
    setTier2Submitting(true);
    setTier2Error(null);
    try {
      // Reuse /api/v1/waitlist (mismo endpoint que la country gate). Para
      // distinguir leads de salud sin cambiar schema, encode la patología
      // y datos del perro en pet_name. Ej:
      const contextParts = [
        'salud-tier2',
        `pathology=${tier2SelectedKeys.join('+')}`,
        formData.peso ? `peso=${formData.peso}kg` : null,
        formData.raza ? `raza=${formData.raza}` : null,
        formData.ageCategory ? `edad=${formData.ageCategory}` : null,
      ].filter((part): part is string => Boolean(part));
      const res = await joinWaitlist({
        email: tier2Email.trim(),
        petName: nombre,
        animalType: 'perro',
        country: 'ES',
        petWeightKg: formData.peso,
        context: contextParts.join(' | '),
      });
      setTier2AlreadyRegistered(!!res?.already_registered);
      setTier2Submitted(true);
      updateFormData({ email: tier2Email.trim() });
    } catch (err) {
      console.error('tier2_waitlist_failed', err);
      setTier2Error(t('p8.tier2_error', 'No pudimos enviar tu email. Inténtalo de nuevo.'));
    } finally {
      setTier2Submitting(false);
    }
  };

  const handleRemoveTier2 = () => {
    // Permite al usuario deseleccionar todas las patologías tier 2 si se
    // equivocó — vuelve al estado normal y el CTA se reactiva.
    const next = new Set(selectedKeys);
    TIER_2_KEYS.forEach((k) => next.delete(k));
    setSelectedKeys(next);
    const nextPathologies = pathologiesFromKeys(next);
    updateFormData({
      healthGateAnswer: 'si',
      pathologies: nextPathologies,
      subPathologies: keepSubPathologiesFor(nextPathologies),
    });
    setTier2Submitted(false);
    setTier2Dismissed(false);
    setTier2Error(null);
  };

  // Tras captar el email, quitamos la condición bloqueante para no dejar el CTA
  // desactivado sin una acción visible en pantalla.
  const handleDismissTier2 = () => handleRemoveTier2();

  // Coherente con click-fuera y con el botón "No, quitar selección": los tres cierran deseleccionando.
  useEffect(() => {
    if (!hasAnyTier2 || gate !== 'si') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (tier2Submitted) handleDismissTier2();
        else handleRemoveTier2();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyTier2, gate, tier2Submitted]);

  const missingReason =
    gate === null
      ? t('p8.missing_gate', 'Indica si {nombre} tiene alguna condición de salud', { nombre })
      : gate === 'si' && selectedKeys.size === 0
        ? t('p8.missing_pathology', 'Selecciona al menos una condición')
        : hasAnyTier2
          ? t('p8.tier2_blocking', 'Estamos preparando recetas para esa condición — déjanos tu email')
          : undefined;

  return (
    <FunnelLayout
      step={isKcalMode ? 7 : 8}
      useTwoPhase
      onContinue={goToNextStep}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={insightsPanel}
    >
      <div className="flex flex-col gap-5">
        {/* Gate question — con foto de raza para personalizar la pregunta */}
        <div className="flex flex-col items-center gap-3 text-center">
          <BreedPhoto breedKey={formData.raza} alt={nombre} className="w-16 h-16" />
          <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-black leading-tight">
            {t('p8.gate_title', '¿Tiene {nombre} alguna enfermedad diagnosticada?', { nombre })}
          </h1>
          <p className="font-poppins text-sm text-black/50">
            {t('p8.gate_subtitle', 'Enfermedades, intolerancias digestivas, problemas de piel, etc.')}
          </p>

          {/* Gate pills — No first to reduce cognitive load (most users have no conditions) */}
          <div
            className={`grid grid-cols-3 gap-2 rounded-[18px] transition-all ${
              attempted && gate === null ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-2' : ''
            }`}
          >
            {(
              [
                { value: 'no', label: t('p8.gate_no', 'No') },
                { value: 'si', label: t('p8.gate_si', 'Sí') },
                { value: 'no_se', label: t('p8.gate_no_se', 'No lo sé') },
              ] as { value: GateAnswer; label: string }[]
            ).map(({ value, label }) => (
              <FunnelPillButton
                key={value}
                active={gate === value}
                onClick={() => handleGate(value)}
                className="py-3 px-2 text-base font-semibold"
              >
                {label}
              </FunnelPillButton>
            ))}
          </div>

          {/* "Why we ask" affordance */}
          <button
            type="button"
            onClick={() => setShowWhyModal(true)}
            className="font-poppins text-xs text-[#008BBC] underline self-center"
          >
            {t('p8.why_link', '¿Por qué lo pedimos?')}
          </button>
        </div>

        {/* Callout for "No lo sé" — reassuring message shown instead of grid */}
        {gate === 'no_se' && (
          <div className="bg-white border border-[#90D1E8]/40 rounded-2xl p-4 text-center shadow-sm">
            <p className="font-poppins text-sm text-black/70 leading-snug">
              {t(
                'p8.no_se_message',
                'Puede que {nombre} haya entrado a formar parte de tu familia hace poco o esté en proceso de diagnóstico. ¡No pasa nada! Puedes actualizar esta información más adelante desde tu cuenta.',
                { nombre },
              )}
            </p>
          </div>
        )}

        {/* Flat pathology grid — only shown after "Sí". Tier subgroups
            ("Ajustamos la dieta", "Requiere atención veterinaria",
            "Condición compleja") are intentionally hidden so the user
            doesn't perceive an implicit hierarchy of severity between
            conditions. The Tier 2 / Tier 3 warning modals still trigger
            on selection — that logic is preserved. */}
        {gate === 'si' &&
          (() => {
            const renderCard = (entry: (typeof PATHOLOGY_CATALOG)[0]) => {
              const isSelected = selectedKeys.has(entry.key);
              return (
                <button
                  key={entry.key}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => handlePathologyToggle(entry.key)}
                  className={`relative flex min-h-[132px] w-full flex-col items-center justify-center gap-1 rounded-[18px] px-2 py-2.5 text-center transition-all duration-200 sm:min-h-[160px] sm:rounded-[20px] sm:gap-1.5 sm:py-4 ${
                    isSelected
                      ? 'bg-white border-[2.5px] border-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)] scale-[1.03]'
                      : 'bg-[#FAEDE1] border-2 border-transparent hover:border-[#90D1E8] hover:scale-105 hover:shadow-md'
                  }`}
                >
                  {isSelected && (
                    <span
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center text-white text-xs font-bold shadow z-20"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                  )}
                  {entry.icon ? (
                    <span
                      className="flex h-[74px] w-[74px] items-center justify-center overflow-visible sm:h-24 sm:w-24"
                      aria-hidden="true"
                    >
                      <img src={entry.icon} alt="" className="h-full w-full scale-[1.42] object-contain" />
                    </span>
                  ) : (
                    <span
                      className="flex h-[74px] w-[74px] items-center justify-center text-5xl leading-none sm:h-24 sm:w-24 sm:text-6xl"
                      aria-hidden="true"
                    >
                      {entry.emoji}
                    </span>
                  )}
                  <span
                    className={`font-fredoka text-xs sm:text-sm font-medium leading-tight ${isSelected ? 'text-[#008BBC]' : 'text-black/70'}`}
                  >
                    {entry.label}
                  </span>
                </button>
              );
            };
            const allEntries = PATHOLOGY_CATALOG.filter((e) => e.key !== 'otra');
            return (
              <div
                className={`rounded-[22px] transition-all ${
                  attempted && selectedKeys.size === 0 ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-2' : ''
                }`}
              >
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">{allEntries.map(renderCard)}</div>
              </div>
            );
          })()}

        {gate === 'si' && selectedSubPathologyGroups.length > 0 && (
          <div className="space-y-3 rounded-2xl border border-[#90D1E8]/50 bg-white p-4 shadow-sm">
            <div>
              <p className="font-fredoka text-sm font-semibold text-black/80">
                {t('p8.subpathologies_title', '¿Puedes concretar un poco más?')}
              </p>
              <p className="font-poppins text-xs text-black/50">
                {t('p8.subpathologies_help', 'Es opcional, pero nos ayuda a aplicar las recomendaciones correctas.')}
              </p>
            </div>
            {selectedSubPathologyGroups.map((group) => (
              <fieldset key={group.value} className="space-y-2">
                <legend className="font-fredoka text-xs font-medium text-black/65">{group.label}</legend>
                <div className="flex flex-wrap gap-2">
                  {(group.sub_pathologies || []).map((subPathology) => {
                    const selected = (formData.subPathologies?.[group.value] || []).includes(subPathology.value);
                    return (
                      <button
                        key={subPathology.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => handleSubPathologyToggle(group.value, subPathology.value)}
                        className={`rounded-full border px-3 py-2 font-poppins text-xs transition-colors ${
                          selected
                            ? 'border-[#008BBC] bg-[#008BBC] text-white'
                            : 'border-[#90D1E8] bg-white text-black/70 hover:bg-[#90D1E8]/10'
                        }`}
                      >
                        {subPathology.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>
        )}

        {/* ── "Otra" — botón al fondo que abre popup de búsqueda ──────────── */}
        {gate === 'si' &&
          (selectedKeys.has('otra') ? (
            <div className="flex items-center justify-between gap-3 bg-white border border-[#008BBC]/30 rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex-1 min-w-0">
                <p className="font-fredoka text-sm font-medium text-[#008BBC]">
                  {t('p8.otra_chip_label', 'Otra condición')} ✓
                </p>
                {formData.otherPathologyText && (
                  <p className="font-poppins text-xs text-black/60 truncate mt-0.5">{formData.otherPathologyText}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleOtraRemove}
                className="flex-shrink-0 text-black/40 hover:text-red-500 transition-colors text-lg font-bold"
                aria-label={t('p8.otra_chip_remove_aria', 'Quitar condición')}
              >
                ×
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowOtraModal(true)}
              className="w-full flex items-center justify-between gap-3 rounded-full border-2 border-[#E9E3D7] bg-white px-5 py-3 transition-all hover:border-[#90D1E8] sm:rounded-2xl sm:border-dashed sm:border-[#008BBC]/30 sm:bg-white/60 sm:px-4"
            >
              <span className="flex min-w-0 items-center gap-2 font-fredoka text-base font-semibold text-black/65 sm:text-sm sm:font-normal sm:text-black/60">
                <span aria-hidden="true">🔍</span>
                <span className="sm:hidden">{t('p8.otra_button_mobile', 'Otra condición')}</span>
                <span className="hidden sm:inline">
                  {t('p8.otra_button', '¿No encuentras la condición de {nombre}?', { nombre })}
                </span>
              </span>
              <span className="text-[#008BBC] font-bold text-sm flex-shrink-0">{t('p8.otra_add', 'Añadir')}</span>
            </button>
          ))}

        {/* ── Insights mobile — trigger que abre popup ──────────────────────
            Antes el panel se renderizaba inline pero ocupaba demasiado en
            móvil. Ahora hay un trigger pequeño y el contenido completo
            vive en un modal. Desktop sigue mostrándolo inline en sidebar. */}
        {tier1Insights.length > 0 && (
          <button
            type="button"
            onClick={() => setShowInsightsModal(true)}
            className="lg:hidden flex items-center justify-between gap-2 w-full px-4 py-3 rounded-2xl bg-white border border-[#90D1E8]/40 shadow-sm hover:bg-[#90D1E8]/5 transition-colors"
          >
            <span className="font-fredoka text-sm font-medium text-[#008BBC] text-left">
              {t('p8.insights_mobile_trigger', 'Cómo ayudará nuestra comida a que {nombre} se ponga mejor', { nombre })}
            </span>
            <span className="text-[#008BBC] text-lg flex-shrink-0" aria-hidden="true">
              →
            </span>
          </button>
        )}
      </div>

      {/* ── Modal "Otra condición" ──────────────────────────────────────────
          Popup con campo de búsqueda libre + comentarios. Si vemos alguna
          compatibilidad con nuestras recetas, lo revisamos antes del envío. */}
      {showOtraModal &&
        (() => {
          const suggestions = otraDropdownOpen ? searchConditions(otraSearch) : [];
          const isExcluded = otraSelected !== null && excludedConditions.includes(otraSelected.key);
          const canConfirm = !isExcluded && otraSearch.trim().length >= 2;
          return (
            <div
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
              onClick={() => setShowOtraModal(false)}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="p8-otra-modal-title"
                className="bg-white rounded-[20px] p-6 max-w-md w-full shadow-2xl space-y-4 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setShowOtraModal(false)}
                  aria-label={t('p8.close_modal_aria', 'Cerrar')}
                  className="absolute top-3 right-3 w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center text-black/50 hover:text-black/80 transition-colors"
                >
                  <span className="text-lg">×</span>
                </button>

                <div className="pr-6">
                  <h3 id="p8-otra-modal-title" className="font-fredoka text-lg font-semibold text-[#1a1a1a]">
                    {t('p8.otra_modal_title', '¿Qué condición tiene {nombre}?', { nombre })}
                  </h3>
                  <p className="font-poppins text-xs text-black/50 mt-1">
                    {t('p8.otra_modal_subtitle', 'Si vemos alguna compatibilidad con nuestras recetas, te llamaremos.')}
                  </p>
                </div>

                {/* ── Buscador con autocompletar ─────────────────────────── */}
                <div>
                  <label className="block font-fredoka text-sm font-medium text-black/70 mb-1">
                    {t('p8.otra_search_label', 'Nombre de la enfermedad o condición')}
                  </label>
                  <div className="relative">
                    <input
                      ref={otraInputRef}
                      type="text"
                      value={otraSearch}
                      onChange={(e) => handleOtraSearchChange(e.target.value)}
                      onFocus={() => otraSearch.trim().length >= 2 && setOtraDropdownOpen(true)}
                      placeholder={t('p8.otra_search_placeholder', 'Ej: luxación de rótula, megaesófago, atopia…')}
                      className="w-full px-3 py-2.5 text-sm font-poppins border-2 border-[#90D1E8]/60 rounded-[12px] focus:outline-none focus:border-[#008BBC] focus:ring-2 focus:ring-[#008BBC]/20"
                      autoComplete="off"
                    />
                    {suggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-[#008BBC] rounded-[14px] overflow-hidden shadow-lg">
                        {suggestions.map((entry) => (
                          <button
                            key={entry.key}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleOtraSelectCondition(entry)}
                            className="w-full px-4 py-2.5 text-left font-poppins text-sm hover:bg-[#90D1E8]/20 transition-all text-black"
                          >
                            {entry.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Condición excluida: captura email ─────────────────── */}
                {isExcluded && !otraExcludedSubmitted && (
                  <form onSubmit={handleSubmitOtraExcluded} className="space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="font-fredoka text-sm font-semibold text-amber-800">
                        {t('p8.otra_excluded_title', 'Aún no tenemos una receta compatible con {condition}.', {
                          condition: otraSelected!.label,
                        })}
                      </p>
                      <p className="font-poppins text-xs text-amber-700 mt-0.5">
                        {t('p8.otra_excluded_body', 'Déjanos tu email y te avisamos cuando la desarrollemos.')}
                      </p>
                    </div>
                    <input
                      type="email"
                      value={otraExcludedEmail}
                      onChange={(e) => setOtraExcludedEmail(e.target.value)}
                      placeholder={t('p8.otra_excluded_placeholder', 'tu@email.com')}
                      required
                      className="w-full px-3 py-2.5 text-sm font-poppins border-2 border-[#90D1E8]/60 rounded-[12px] focus:outline-none focus:border-[#008BBC] focus:ring-2 focus:ring-[#008BBC]/20"
                    />
                    {otraExcludedError && <p className="font-poppins text-xs text-red-500">{otraExcludedError}</p>}
                    <button
                      type="submit"
                      disabled={!otraExcludedEmail.trim() || otraExcludedSubmitting}
                      className="w-full px-5 py-3 rounded-xl font-fredoka text-sm font-semibold bg-[#008BBC] text-white hover:bg-[#007aa8] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                    >
                      {otraExcludedSubmitting
                        ? t('p8.otra_excluded_submitting', 'Enviando…')
                        : t('p8.otra_excluded_submit', 'Avísame cuando esté lista')}
                    </button>
                  </form>
                )}

                {isExcluded && otraExcludedSubmitted && (
                  <div className="text-center space-y-2 py-2">
                    <p className="text-3xl">✅</p>
                    <p className="font-fredoka text-base font-semibold text-[#1a1a1a]">
                      {t('p8.otra_excluded_thanks_title', '¡Apuntado!')}
                    </p>
                    <p className="font-poppins text-xs text-black/50">
                      {t(
                        'p8.otra_excluded_thanks_body',
                        'Te escribiremos en cuanto tengamos una receta adaptada para {condition}.',
                        { condition: otraSelected!.label },
                      )}
                    </p>
                    {otraExcludedAlreadyRegistered && (
                      <p className="font-poppins text-xs text-[#008BBC]">
                        {t(
                          'p8.otra_excluded_already_updated',
                          'Ya estabas en nuestra lista; hemos actualizado tus datos 🐾',
                        )}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowOtraModal(false)}
                      className="mt-2 w-full px-5 py-2 rounded-xl font-fredoka text-sm font-medium text-black/50 hover:bg-gray-50 transition-all"
                    >
                      {t('p8.otra_excluded_close', 'Cerrar')}
                    </button>
                  </div>
                )}

                {/* ── Condición normal: comentarios + confirmar ──────────── */}
                {!isExcluded && (
                  <>
                    <div>
                      <label className="block font-fredoka text-sm font-medium text-black/70 mb-1">
                        {t('p8.otra_comment_label', 'Comentarios adicionales (opcional)')}
                      </label>
                      <textarea
                        value={otraComment}
                        onChange={(e) => setOtraComment(e.target.value)}
                        placeholder={t('p8.otra_comment_placeholder', 'Añade cualquier detalle que pueda ayudarnos…')}
                        rows={2}
                        maxLength={400}
                        className="w-full px-3 py-2 text-sm font-poppins border-2 border-[#90D1E8]/40 rounded-[12px] focus:outline-none focus:border-[#008BBC] focus:ring-2 focus:ring-[#008BBC]/20 resize-none"
                      />
                    </div>
                    <div className="flex flex-col gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleOtraConfirm}
                        disabled={!canConfirm}
                        className="w-full px-5 py-3 rounded-xl font-fredoka text-sm font-semibold bg-[#008BBC] text-white hover:bg-[#007aa8] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                      >
                        {t('p8.otra_confirm', 'Añadir condición')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowOtraModal(false)}
                        className="w-full px-5 py-2 rounded-xl font-fredoka text-sm font-medium text-black/50 hover:bg-gray-50 transition-all"
                      >
                        {t('p8.otra_cancel', 'Cancelar')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

      {/* ── Tier 2 waitlist — modal popup ────────────────────────────────
          Cuando el usuario marca renal/hepática/pancreática, abrimos un
          modal sobre la pantalla en vez de mostrar el form inline. Mucho
          más visible y fuerza la atención al captura de email. Cerrar
          (✕ / click fuera / "No, quitar selección") deselecciona la
          patología tier 2. Submit guarda en /api/v1/waitlist y muestra
          confirmación dentro del mismo modal. */}
      {!isKcalMode && hasAnyTier2 && gate === 'si' && !tier2Dismissed && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={tier2Submitted ? handleDismissTier2 : handleRemoveTier2}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="p8-tier2-modal-title"
            className="bg-white rounded-[20px] p-6 max-w-md w-full shadow-2xl space-y-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={tier2Submitted ? handleDismissTier2 : handleRemoveTier2}
              aria-label={t('p8.close_modal_aria', 'Cerrar')}
              className="absolute top-3 right-3 w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center text-black/50 hover:text-black/80 transition-colors"
            >
              <span className="text-lg">×</span>
            </button>

            <div className="flex items-start gap-3">
              <span className="text-3xl flex-shrink-0" aria-hidden="true">
                🧑‍⚕️
              </span>
              <div className="space-y-1.5 pr-6">
                <h3
                  id="p8-tier2-modal-title"
                  className="font-fredoka text-lg font-semibold text-[#1a1a1a] leading-snug"
                >
                  {t('p8.tier2_title', 'Estamos preparando recetas específicas para {pathology}', {
                    pathology: tier2Labels.toLowerCase(),
                  })}
                </h3>
                <p className="font-poppins text-sm text-black/70 leading-snug">
                  {t(
                    'p8.tier2_body',
                    'Aún no tenemos un plan estándar adecuado para {nombre}. Déjanos tu email y te avisaremos en cuanto esté disponible.',
                    { nombre },
                  )}
                </p>
              </div>
            </div>

            {!tier2Submitted ? (
              <form onSubmit={handleSubmitTier2} className="space-y-3" data-testid="p8-tier2-form">
                <input
                  type="email"
                  required
                  value={tier2Email}
                  onChange={(e) => setTier2Email(e.target.value)}
                  placeholder={t('p8.tier2_email_placeholder', 'tu@email.com')}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white font-poppins text-sm focus:outline-none focus:ring-2 focus:ring-[#008BBC]/30 focus:border-[#008BBC]"
                  autoComplete="email"
                  inputMode="email"
                />
                {tier2Error && (
                  <p className="font-poppins text-xs text-red-600" role="alert">
                    {tier2Error}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    type="submit"
                    disabled={tier2Submitting || !tier2Email.trim()}
                    className="w-full px-5 py-3 rounded-xl font-fredoka text-sm font-semibold bg-[#008BBC] text-white hover:bg-[#007aa8] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                  >
                    {tier2Submitting
                      ? t('p8.tier2_submitting', 'Enviando…')
                      : t('p8.tier2_submit', 'Avísame cuando esté listo')}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveTier2}
                    className="w-full px-5 py-3 rounded-xl font-fredoka text-sm font-medium text-[#008BBC] hover:bg-[#008BBC]/5 transition-all"
                  >
                    {t('p8.tier2_unselect', 'No, quitar selección')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-[#008BBC]/10 border border-[#008BBC]/30 rounded-xl p-4 text-center space-y-2">
                <span className="text-3xl" aria-hidden="true">
                  ✉️
                </span>
                <p className="font-fredoka text-base font-semibold text-[#008BBC]">
                  {t('p8.tier2_thanks_title', '¡Apuntado!')}
                </p>
                <p className="font-poppins text-sm text-black/70">
                  {t('p8.tier2_thanks_body', 'Te avisaremos en cuanto tengamos un plan adecuado para {nombre}.', {
                    nombre,
                  })}
                </p>
                {tier2AlreadyRegistered && (
                  <p className="font-poppins text-sm text-[#008BBC]">
                    {t('p8.tier2_already_updated', 'Ya estabas en nuestra lista; hemos actualizado tus datos 🐾')}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleDismissTier2}
                  className="mt-2 px-5 py-2 rounded-xl font-fredoka text-sm font-medium text-[#008BBC] hover:bg-[#008BBC]/5 transition-colors"
                >
                  {t('p8.tier2_close_after_thanks', 'Cerrar')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tier 2 warning modal ─────────────────────────────────────────────── */}
      {modalTier === 2 && (
        <PathologyModal
          icon="⚠️"
          titleId="p8-tier2-title"
          title={t('p8.tier2_modal_title', 'Recomendamos consultar con tu veterinario')}
          body={getTierCopy(2)}
          whatsappUrl={whatsappUrl}
          whatsappText={encodeURIComponent(
            t(
              'p8.whatsapp_pretext',
              'Hola, necesito asesoramiento nutricional para mi perro con una condición de salud especial',
            ),
          )}
          whatsappLabel={t('p8.otra_whatsapp', 'Hablar por WhatsApp')}
          secondaryLabel={t('p8.tier2_continue', 'Continuar el análisis')}
          onSecondary={handleModalContinue}
          onDismiss={handleModalDismiss}
        />
      )}

      {/* ── Tier 3 escalation modal ──────────────────────────────────────────── */}
      {modalTier === 3 && (
        <PathologyModal
          icon="🎗️"
          titleId="p8-tier3-title"
          title={t('p8.tier3_modal_title', 'No tenemos receta compatible')}
          body={getTierCopy(3)}
          whatsappUrl={whatsappUrl}
          whatsappText={encodeURIComponent(
            t(
              'p8.whatsapp_pretext',
              'Hola, necesito asesoramiento nutricional para mi perro con una condición de salud especial',
            ),
          )}
          whatsappLabel={t('p8.otra_whatsapp', 'Hablar por WhatsApp')}
          secondaryLabel={t('p8.tier3_close', 'Cerrar')}
          onSecondary={handleModalDismiss}
          onDismiss={handleModalDismiss}
        />
      )}

      {/* ── "Por qué lo pedimos" info modal ─────────────────────────────────── */}
      {showWhyModal && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4"
          onClick={handleWhyModalClose}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title-why"
            className="bg-white rounded-[20px] p-6 max-w-sm w-full shadow-xl space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="modal-title-why" className="font-fredoka text-lg font-semibold text-black">
              {t('p8.why_modal_title', '¿Por qué lo pedimos?')}
            </h3>
            <p className="font-poppins text-sm text-black/70 leading-relaxed">
              {t(
                'p8.why_modal_body',
                '1 de cada 3 perros tiene sensibilidad digestiva no diagnosticada. Formular el plan de {nombre} sin esta información puede dejar síntomas sin atender.',
                { nombre },
              )}
            </p>
            <button
              type="button"
              onClick={handleWhyModalClose}
              className="w-full py-2.5 bg-[#008BBC] text-white font-fredoka font-medium rounded-[14px] hover:bg-[#007aa8] transition-colors"
            >
              {t('p8.why_modal_close', 'Entendido')}
            </button>
          </div>
        </div>
      )}

      {/* ── Insights modal (mobile) — desktop sigue mostrando el panel
          inline en el sidebar; en móvil el panel se abre desde el
          trigger "Cómo ayudará nuestra comida..." */}
      {showInsightsModal && tier1Insights.length > 0 && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4 lg:hidden"
          onClick={() => setShowInsightsModal(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="p8-insights-modal-title"
            className="bg-white rounded-[20px] p-5 max-w-md w-full shadow-xl space-y-4 max-h-[85vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowInsightsModal(false)}
              aria-label={t('p8.close_modal_aria', 'Cerrar')}
              className="absolute top-3 right-3 w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center text-black/50 hover:text-black/80 transition-colors"
            >
              <span className="text-lg">×</span>
            </button>
            <h3 id="p8-insights-modal-title" className="font-fredoka text-base font-semibold text-black pr-8">
              {t('p8.insights_modal_title', 'Cómo ayudará nuestra comida a {nombre}', { nombre })}
            </h3>
            <ul className="space-y-3">
              {tier1Insights.map((ins) => (
                <li key={ins.key} className="space-y-1">
                  <p className="font-poppins text-xs text-black/70 leading-snug">
                    <span className="font-fredoka font-semibold text-black/85">{ins.label}:</span> {ins.why}
                  </p>
                  {ins.supplement && (
                    <p className="font-poppins text-xs text-[#008BBC] flex items-center gap-1">
                      <span aria-hidden="true">💊</span>
                      <span>
                        {t('p8.insights_supplement_prefix', 'Suplemento sugerido:')} {ins.supplement}
                      </span>
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setShowInsightsModal(false)}
              className="w-full py-2.5 bg-[#008BBC] text-white font-fredoka font-medium rounded-[14px] hover:bg-[#007aa8] transition-colors"
            >
              {t('p8.insights_modal_close', 'Cerrar')}
            </button>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
};

export default P8Salud;
